import { useEffect, useState } from "react";
import { fetchApplication, revealOwnAnswers, type PortalApplicationDetail } from "./api.js";
import { ArrowLeft, CardSkeleton, Eye, Notice, formatDate, formatValue } from "./ui.js";

/**
 * One application, rendered against the form version it was submitted under — never against the
 * current one. A published `FormVersion` is immutable and a submission always renders against its
 * own (CLAUDE.md), which is why the version number is on the page: two applications to the same
 * programme in different years can legitimately show different questions.
 */
export const ApplicationDetail = ({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}): JSX.Element => {
  const [detail, setDetail] = useState<PortalApplicationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setDetail(null);
    void fetchApplication(id)
      .then((result) => {
        if (live) setDetail(result);
      })
      .catch((caught: unknown) => {
        if (live) {
          setError(
            caught instanceof Error ? caught.message : "This application could not be loaded.",
          );
        }
      });
    return () => {
      live = false;
    };
  }, [id]);

  return (
    <div>
      <button type="button" onClick={onBack} className="sga-link inline-flex items-center gap-1.5">
        <ArrowLeft className="h-3.5 w-3.5" />
        All applications
      </button>

      {error ? (
        <div className="mt-4">
          <Notice tone="error">{error}</Notice>
        </div>
      ) : !detail ? (
        <div className="mt-4">
          <CardSkeleton rows={1} />
        </div>
      ) : (
        <div className="sga-card mt-4">
          <div className="sga-tint rounded-t-[13px] p-5">
            <h1 className="font-heading text-xl font-semibold">{detail.formTitle}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {detail.cycle && <span className="sga-badge">{detail.cycle}</span>}
              <span className="sga-badge">Form version {detail.formVersion}</span>
              <span className="sga-muted text-xs">
                {detail.submittedAt
                  ? `Submitted ${formatDate(detail.submittedAt)}`
                  : "Not submitted"}
              </span>
            </div>
            {detail.reference && (
              <p className="mt-3 text-sm">
                <span className="sga-muted">Reference number </span>
                <strong className="sga-reference" style={{ color: "var(--sga-primary)" }}>
                  {detail.reference}
                </strong>
              </p>
            )}
          </div>

          <dl className="p-5">
            {detail.answers.map((answer, index) => (
              <div
                key={answer.key}
                className={`grid gap-1 py-3 sm:grid-cols-[minmax(0,14rem)_1fr] sm:gap-4 ${
                  index > 0 ? "sga-divider" : ""
                }`}
              >
                <dt className="sga-muted text-sm">{answer.label}</dt>
                <dd className="text-sm">
                  {answer.isSensitive ? (
                    <OwnSensitiveAnswer submissionId={id} fieldKey={answer.key} />
                  ) : (
                    formatValue(answer.value)
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
};

/**
 * The applicant's own encrypted answer — their NIN, their bank account number.
 *
 * Hidden until asked for, but only because a portal open on a shared laptop should not have a bank
 * account number sitting on screen. It is their own data, so nothing is justified and no reason is
 * asked: one tap shows it. The value lives in this component's state and goes when the screen does.
 *
 * Showing it matters beyond curiosity — a typo in an account number is invisible to the applicant
 * everywhere else, and is discovered when a payment fails.
 */
const OwnSensitiveAnswer = ({
  submissionId,
  fieldKey,
}: {
  submissionId: string;
  fieldKey: string;
}): JSX.Element => {
  const [value, setValue] = useState<string | null>(null);
  const [state, setState] = useState<"hidden" | "loading" | "shown" | "error">("hidden");

  if (state === "shown") {
    return (
      <span className="flex flex-wrap items-center gap-3">
        <span className="sga-reference">{value ?? "—"}</span>
        <button
          type="button"
          className="sga-link"
          onClick={() => {
            setValue(null);
            setState("hidden");
          }}
        >
          Hide
        </button>
      </span>
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-3">
      <span className="sga-muted tracking-[0.2em]" aria-hidden="true">
        ••••••••
      </span>
      <button
        type="button"
        disabled={state === "loading"}
        onClick={() => {
          setState("loading");
          revealOwnAnswers(submissionId, [fieldKey])
            .then((result) => {
              setValue(result.values[fieldKey] ?? null);
              setState("shown");
            })
            .catch(() => setState("error"));
        }}
        className="sga-btn sga-btn--secondary sga-btn--sm"
      >
        <Eye className="h-3.5 w-3.5" />
        {state === "loading" ? "Showing…" : "Show"}
      </button>
      {state === "error" && (
        <span role="alert" className="text-xs" style={{ color: "var(--sga-error)" }}>
          Could not show that just now.
        </span>
      )}
    </span>
  );
};
